// Service page — the full answer for one deployable unit:
//   what's deployed where (env matrix), what happened recently (history),
//   and what was shipped (artifact / plan / approval details).
//
// Replaces level 2 of the legacy JobIdModal AND the "historical takeover"
// mode: history is fetched per-service into its own slice, the live record
// list is never replaced, and this page has a real URL.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Construction as JobsIcon,
  GridView as ServicesIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { groupServices, isActiveDeploy, ts } from '../lib/grouping';
import { deploymentKey, historicalKey, serviceRecordMatches } from '../lib/records';
import { getColors, rounded } from '../theme/tokens';
import PageHeader from '../components/PageHeader';
import TypeBadge from '../components/TypeBadge';
import EnvMatrix from '../components/EnvMatrix';
import DeploymentRow from '../components/DeploymentRow';
import DeploymentDetailsDialog from '../components/DeploymentDetailsDialog';
import EmptyState from '../components/EmptyState';
import { formatTimestamp } from '../lib/format';
import { jobPath } from '../lib/jobPath';
import { DeploymentRecord } from '../../types/DeploymentRecord';

const ServicePage: React.FC = () => {
  const { app = '', jobId = '' } = useParams<{ app: string; jobId: string }>();
  const [searchParams] = useSearchParams();
  const highlightDep = searchParams.get('dep') || '';
  // An app can have two jobs with the same job_id but different types; `?type=`
  // disambiguates which one this page targets.
  const jobType = searchParams.get('type') || undefined;
  const theme = useTheme();
  const c = getColors(theme.palette.mode);

  const {
    records,
    staleCurrentIds,
    wsStatus,
    fetchHistorical,
    historical,
    fetchArtifactFor,
    githubLinkFor,
    artifactStatusFor,
  } = useDeploymentData();

  const [historyLimit, setHistoryLimit] = useState(25);
  const [selectedRecord, setSelectedRecord] = useState<DeploymentRecord | null>(null);

  const serviceRecords = useMemo(
    () => records.filter(r => serviceRecordMatches(r, app, jobId, jobType)),
    [records, app, jobId, jobType]
  );

  // `group` (raw) drives existence, the full service string for history, and
  // type metadata. `visibleGroup` powers the current-state surfaces (env
  // matrix, Deploying Now) with stale/superseded deploys dropped — so history
  // still loads even when a service's only current record has aged off.
  const group = useMemo(() => groupServices(serviceRecords)[0], [serviceRecords]);
  const visibleServiceRecords = useMemo(
    () => serviceRecords.filter(r => !r.id || !staleCurrentIds.has(r.id)),
    [serviceRecords, staleCurrentIds]
  );
  const visibleGroup = useMemo(
    () => groupServices(visibleServiceRecords)[0],
    [visibleServiceRecords]
  );
  const fullService = group?.service || '';

  // Plan link per env, taken from the most recent record for that env that has
  // one — the representative byEnv record isn't guaranteed to be the one that
  // carries s3_plan_link, so scan all the service's records.
  const planLinkByEnv = useMemo(() => {
    const latest = new Map<string, { ts: number; link: string }>();
    serviceRecords.forEach(r => {
      if (!r.s3_plan_link) return;
      const env = r.envName || r.env || '';
      const t = new Date(r.timestamp).getTime();
      const cur = latest.get(env);
      if (!cur || t > cur.ts) latest.set(env, { ts: t, link: r.s3_plan_link });
    });
    const m = new Map<string, string>();
    latest.forEach((v, env) => m.set(env, v.link));
    return m;
  }, [serviceRecords]);

  // Per-env github-commit button state. Artifact data is fetched on click only
  // (see EnvMatrix onGithubFetch), so this just reflects the current cache.
  const githubByEnv = useMemo(() => {
    const m = new Map<string, { status: ReturnType<typeof artifactStatusFor>; url?: string }>();
    if (visibleGroup) {
      visibleGroup.byEnv.forEach((rec, env) => {
        m.set(env, { status: artifactStatusFor(rec), url: githubLinkFor(rec) });
      });
    }
    return m;
  }, [visibleGroup, artifactStatusFor, githubLinkFor]);
  const histKey = historicalKey(app, fullService);
  const history = historical.get(histKey);

  // Fetch history when connected / when the limit changes.
  useEffect(() => {
    if (wsStatus === 'connected' && fullService) {
      fetchHistorical(app, fullService, historyLimit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsStatus, app, fullService, historyLimit]);

  const latestRecord = useMemo(() => {
    if (serviceRecords.length === 0) return null;
    return [...serviceRecords].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[0];
  }, [serviceRecords]);

  // In-progress deployments for this service — surfaced prominently up top so
  // an active deploy's pipeline is impossible to miss.
  const deployingRecords = useMemo(
    () =>
      visibleServiceRecords
        .filter(r => isActiveDeploy(r))
        .sort((a, b) => ts(b.timestamp) - ts(a.timestamp)),
    [visibleServiceRecords]
  );

  const historyRecords = useMemo(() => {
    const recs = history?.records || [];
    return [...recs].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [history]);

  // Terraform approval metadata from the most recent record carrying it.
  // Pick by max timestamp so the displayed approval is deterministic rather
  // than whichever decision-bearing record happens to come first.
  const approval = useMemo(() => {
    const withDecision = [...serviceRecords, ...historyRecords].filter(r => r.decision);
    if (withDecision.length === 0) return null;
    return withDecision.reduce((latest, r) =>
      ts(r.timestamp) > ts(latest.timestamp) ? r : latest
    );
  }, [serviceRecords, historyRecords]);

  // The dialog follows the live copy of the record it was opened on, so a
  // change that arrives over the stream after the click (the rca map filling
  // in, most of all) shows without reopening it. History is a fetched
  // snapshot, so the click-time record is the fallback.
  const liveSelectedRecord = useMemo(() => {
    if (!selectedRecord?.id) return selectedRecord;
    const live = records.find(r => r.id === selectedRecord.id);
    return live ? { ...selectedRecord, ...live } : selectedRecord;
  }, [selectedRecord, records]);

  if (!group) {
    return (
      <Box>
        <PageHeader
          crumbs={[
            { label: 'Services', to: '/services' },
            { label: app, to: `/services/${encodeURIComponent(app)}` },
            { label: jobId },
          ]}
          title={jobId}
        />
        <EmptyState
          icon={<ServicesIcon />}
          title="No current deployments for this service"
          body="This service has no records in the current dataset. It may have been renamed, or the link may be stale."
          action={
            <Button component={RouterLink} to={`/services/${encodeURIComponent(app)}`} variant="outlined" size="small">
              Back to {app}
            </Button>
          }
        />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={[
          { label: 'Services', to: '/services' },
          { label: app, to: `/services/${encodeURIComponent(app)}` },
          { label: jobId },
        ]}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {jobId}
            <TypeBadge type={group.type} />
          </Box>
        }
        subtitle={`${visibleGroup?.byEnv.size ?? 0} environment${(visibleGroup?.byEnv.size ?? 0) !== 1 ? 's' : ''}`}
        actions={
          <Button
            component={RouterLink}
            to={jobPath(app, jobId, group.type)}
            size="small"
            variant="outlined"
            startIcon={<JobsIcon sx={{ fontSize: 15 }} />}
          >
            Job definition
          </Button>
        }
      />

      {/* Terraform approval details */}
      {approval && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 3, borderRadius: `${rounded.lg}px`, borderColor: c.hairline }}
        >
          <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block', mb: 0.5 }}>
            Latest approval
          </Typography>
          <Typography variant="body2">
            <strong>{approval.decision}</strong>
            {approval.decision_by ? ` by ${approval.decision_by}` : ''}
            {approval.decision_timestamp ? ` · ${formatTimestamp(approval.decision_timestamp)}` : ''}
          </Typography>
        </Paper>
      )}

      {/* Deploying now — pipeline progress for any active deploy */}
      {deployingRecords.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" sx={{ color: '#0b76d1', display: 'block', mb: 1 }}>
            Deploying now
          </Typography>
          <Paper
            variant="outlined"
            sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline, overflow: 'hidden' }}
          >
            {deployingRecords.map((r, i) => (
              <DeploymentRow key={deploymentKey(r, i)} record={r} hideService progress index={i} />
            ))}
          </Paper>
        </Box>
      )}

      {/* Env matrix */}
      <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block', mb: 1 }}>
        Currently deployed
      </Typography>
      <EnvMatrix
        byEnv={visibleGroup?.byEnv ?? new Map()}
        onEnvClick={(_env, record) => setSelectedRecord(record)}
        planLinks={planLinkByEnv}
        github={githubByEnv}
        onGithubFetch={fetchArtifactFor}
      />

      <Divider sx={{ my: 3, borderColor: c.hairline }} />

      {/* History */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Typography variant="overline" sx={{ color: c.inkMuted }}>
          Deployment history
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Select
          size="small"
          value={historyLimit}
          onChange={e => setHistoryLimit(Number(e.target.value))}
          sx={{ fontSize: '0.8rem', height: 30 }}
        >
          {[10, 25, 50, 100].map(n => (
            <MenuItem key={n} value={n} sx={{ fontSize: '0.8rem' }}>
              Last {n}
            </MenuItem>
          ))}
        </Select>
        <Button
          size="small"
          startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
          onClick={() => fetchHistorical(app, fullService, historyLimit)}
          disabled={wsStatus !== 'connected' || history?.loading}
          sx={{ fontSize: '0.78rem' }}
        >
          Refresh
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline, overflow: 'hidden' }}
      >
        {history?.loading && historyRecords.length === 0 ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={40} sx={{ borderRadius: 1, mb: 1 }} />
            ))}
          </Box>
        ) : historyRecords.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              No historical records returned for this service.
            </Typography>
          </Box>
        ) : (
          historyRecords.map((r, i) => {
            const highlighted =
              !!highlightDep &&
              ((r.deployment_id && r.deployment_id.includes(highlightDep)) || r.id === highlightDep);
            return (
              <Box
                key={r.id}
                role="button"
                tabIndex={0}
                aria-label="View deployment details"
                onClick={() => setSelectedRecord(r)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedRecord(r);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: c.hoverWash },
                  ...(highlighted
                    ? { backgroundColor: `${c.primary}14`, borderLeft: `3px solid ${c.primary}` }
                    : {}),
                }}
              >
                <DeploymentRow record={r} hideService details progress index={i} />
              </Box>
            );
          })
        )}
      </Paper>

      <Typography variant="caption" sx={{ opacity: 0.5, display: 'block', mt: 1 }}>
        Showing the last {historyRecords.length} records
        {latestRecord?.timestamp ? ` · live as of ${formatTimestamp(latestRecord.timestamp)}` : ''} · history is
        capped at 100 records per service
      </Typography>

      <DeploymentDetailsDialog record={liveSelectedRecord} onClose={() => setSelectedRecord(null)} />
    </Box>
  );
};

export default ServicePage;
